import type { FilterOption } from '@/components/common/filters/select-filter'
import { m } from '@/lib/paraglide/messages'

// Kênh cho bộ lọc danh sách (single-select). Bỏ "pos" vì kênh này chưa dùng.
// source_channel là free-text nên đây là tập kênh hệ thống cho phép lọc.
export const CHANNEL_FILTER_VALUES: readonly string[] = [
    'kiotviet',
    'direct',
    'online',
    'zalo_mini_app',
    'quick_order',
    'pos_delivery',
    'agent',
    'phone',
    'other',
]

// Ô lọc và chip dùng CHUNG tham số này để trúng cùng một cache — không bắn thêm
// request chỉ để tra tên hãng cho chip.
export const CARRIER_FILTER_QUERY = { per_page: 100 } as const

export function getChannelLabel(channel: string): string {
    switch (channel) {
        case 'direct':
            return m.order_channel_direct()
        case 'phone':
            return m.order_channel_phone()
        case 'online':
            return m.order_channel_online()
        case 'zalo_mini_app':
            return m.order_channel_zalo_mini_app()
        case 'agent':
            return m.order_channel_agent()
        case 'quick_order':
            return m.order_channel_quick()
        case 'pos_delivery':
            return m.order_channel_pos_delivery()
        case 'other':
            return m.order_channel_other()
        default:
            return channel
    }
}

export const CHANNEL_FILTER_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.order_channel_all() },
    { value: 'kiotviet', label: 'KiotViet' },
    ...CHANNEL_FILTER_VALUES.filter((v) => v !== 'kiotviet').map((v) => ({
        value: v,
        label: getChannelLabel(v),
    })),
]

// Chỉ 'all' / 'me': lọc theo một người cụ thể cần quyền xem chéo, mà server ép
// UUID lạ về chính người gọi — bày danh sách người dùng chỉ tạo bộ lọc trông
// chạy được nhưng không ăn (FD-376).
export const CREATOR_FILTER_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.order_creator_filter_all() },
    { value: 'me', label: m.order_creator_filter_me() },
]

/** Chip thanh toán chọn nhiều; không chọn chip nào = không lọc. */
export const PAYMENT_CHIP_OPTIONS = (): FilterOption<string>[] => [
    { value: 'unpaid', label: m.order_payment_chip_unpaid() },
    { value: 'partial', label: m.order_payment_chip_partial() },
    { value: 'paid', label: m.order_payment_chip_paid() },
]

// Nhóm nghiệp vụ khớp enum backend; pushed tách riêng để kế toán thấy backlog ký.
export const EINVOICE_FILTER_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.einvoice_filter_all() },
    { value: 'needs_action', label: m.einvoice_filter_needs_action() },
    { value: 'draft', label: m.einvoice_badge_draft() },
    { value: 'pushed', label: m.einvoice_badge_pushed() },
    { value: 'awaiting', label: m.einvoice_badge_awaiting() },
    { value: 'processing', label: m.einvoice_filter_processing() },
    { value: 'rejected', label: m.einvoice_badge_rejected() },
    { value: 'failed', label: m.einvoice_badge_failed() },
    { value: 'issued', label: m.einvoice_filter_issued() },
    { value: 'manual_issued', label: m.einvoice_badge_manual_issued() },
    { value: 'skipped', label: m.einvoice_badge_skipped() },
    { value: 'none', label: m.einvoice_badge_none() },
]

export const SLA_STATE_FILTER_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.order_sla_filter_all() },
    { value: 'overdue', label: m.sla_overdue() },
    { value: 'warning', label: m.sla_almost_overdue() },
    { value: 'on_time', label: m.order_sla_filter_on_time() },
]

// Nợ lô (FD-372): worklist của kho. Giá trị là CHỮ chứ không phải '1':
// TanStack Router parse `?has_lot_debt=1` thành SỐ 1 và link từ ô KPI sẽ mở
// màn đơn hàng mà không lọc gì. Đổi sang '1' lúc gọi API.
export const LOT_DEBT_FILTER_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.order_lot_debt_filter_all() },
    { value: 'owing', label: m.order_lot_debt_filter_owing() },
]

// Đơn KiotViet còn dòng chưa nối sản phẩm (M-145). Giá trị chữ cùng lý do với
// LOT_DEBT_FILTER_OPTIONS; đổi sang '1' lúc gọi API.
export const KIOTVIET_UNLINKED_FILTER_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.order_kiotviet_unlinked_filter_all() },
    { value: 'pending', label: m.order_kiotviet_unlinked_filter_pending() },
]

// Đơn "Bán ngay" tại quầy (M-166) — bảng nhắc hóa đơn mở danh sách đơn quầy
// chưa phát hành. Giá trị chữ cùng lý do với LOT_DEBT_FILTER_OPTIONS; đổi sang
// '1' lúc gọi API.
export const QUICK_SALE_FILTER_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.order_quick_sale_filter_all() },
    { value: 'only', label: m.order_quick_sale_filter_only() },
]

export const SETTLEMENT_INTENT_OPTIONS = (): FilterOption<string>[] => [
    { value: 'all', label: m.order_settlement_filter_all() },
    { value: 'cash', label: m.payment_method_cash() },
    { value: 'bank_transfer', label: m.payment_method_bank() },
    { value: 'credit', label: m.order_settlement_credit() },
]

/** Ô tick "VTP đang hoàn về" trong danh sách trạng thái (M-86). */
export const VTP_RETURNING_STATUS = 'vtp_returning'

export const STATUS_OPTIONS = (): FilterOption<string>[] => [
    { value: 'draft', label: m.status_order_draft() },
    { value: 'new', label: m.status_order_new() },
    { value: 'awaiting_packaging', label: m.await_filter_option() },
    { value: 'confirmed', label: m.status_order_confirmed() },
    { value: 'stock_out', label: m.status_order_stock_out() },
    { value: 'ready_to_deliver', label: m.status_order_ready_to_deliver() },
    { value: 'in_delivery', label: m.status_order_in_delivery() },
    // Không phải trạng thái đơn: đơn "Đang giao" mà VTP đã chuyển hoàn (502),
    // chưa trả hàng về HTX (504) — server tách khỏi "Đang giao" (M-86).
    { value: VTP_RETURNING_STATUS, label: m.order_status_vtp_returning() },
    { value: 'delivered', label: m.status_order_delivered() },
    { value: 'delivery_failed', label: m.status_order_delivery_failed() },
    { value: 'returned', label: m.dashboard_status_returned() },
    { value: 'cancelled', label: m.status_order_cancelled() },
]

/**
 * Mã trạng thái backend có mà bảng chọn chưa khai — donut ở Tổng quan link
 * sang bằng mã thô (FD-376). Vứt đi thì màn mở lên KHÔNG lọc gì; cho qua và hiện
 * nguyên văn, một mã `snake_case` giữa các nhãn tiếng Việt là dấu hiệu cần khai.
 * Guard query phía backend chỉ nhận mã trong enum `status` của nó, nên mã mới
 * phải khai cả ở đó; thiếu thì danh sách hiện lỗi tải.
 */
export const RAW_STATUS_CODE = /^[a-z][a-z0-9_]{0,39}$/

export const ORDER_PERIODS = [
    'today',
    'yesterday',
    'this_week',
    'month',
    'last_month',
    'all',
    'custom',
] as const
export type OrderPeriod = (typeof ORDER_PERIODS)[number]

/** Lựa chọn nhanh ở ô thời gian; "Tùy chỉnh" là dòng riêng có ô ngày. */
export const PERIOD_PRESET_OPTIONS = (): FilterOption<
    Exclude<OrderPeriod, 'custom'>
>[] => [
    { value: 'today', label: m.warehouse_sales_preset_today() },
    { value: 'yesterday', label: m.warehouse_sales_preset_yesterday() },
    { value: 'this_week', label: m.warehouse_sales_preset_week() },
    { value: 'month', label: m.warehouse_sales_preset_month() },
    { value: 'last_month', label: m.order_period_last_month() },
    { value: 'all', label: m.order_period_all() },
]
